import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import api from '../lib/api';
import { toast } from 'sonner';
import { Home, Search, Loader2, Calendar, User, Building2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

export default function AdminTenancies() {
  const [tenancies, setTenancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    loadTenancies();
  }, [statusFilter]);

  const loadTenancies = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      
      const { data } = await api.get(`/admin/tenancies?${params}`);
      setTenancies(data || []);
    } catch (error) {
      toast.error('Failed to load tenancies');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      'draft': 'bg-slate-100 text-slate-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'active': 'bg-green-100 text-green-800',
      'ending': 'bg-orange-100 text-orange-800',
      'ended': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const filteredTenancies = tenancies.filter(t => 
    !search || 
    t.property_address?.toLowerCase().includes(search.toLowerCase()) ||
    t.tenant_name?.toLowerCase().includes(search.toLowerCase())
  );

  const stats = {
    total: tenancies.length,
    active: tenancies.filter(t => t.status === 'active').length,
    pending: tenancies.filter(t => t.status === 'pending').length,
    ending: tenancies.filter(t => t.status === 'ending').length
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Tenancy Management</h1>
            <p className="text-slate-600">Manage all tenancies across the platform</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Active</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Ending</p>
              <p className="text-2xl font-bold text-orange-600">{stats.ending}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by property or tenant..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm"
              >
                <option value="">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="ending">Ending</option>
                <option value="ended">Ended</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Tenancies Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredTenancies.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                <Home className="w-12 h-12 mb-2 opacity-50" />
                <p>No tenancies found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-slate-600">Property</th>
                      <th className="text-left p-4 font-medium text-slate-600">Tenant</th>
                      <th className="text-left p-4 font-medium text-slate-600">Rent</th>
                      <th className="text-left p-4 font-medium text-slate-600">Start Date</th>
                      <th className="text-left p-4 font-medium text-slate-600">End Date</th>
                      <th className="text-left p-4 font-medium text-slate-600">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredTenancies.map((tenancy) => (
                      <tr key={tenancy.id} className="hover:bg-slate-50">
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-900">
                              {tenancy.property_address || 'Property'}
                            </span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-600">
                              {tenancy.tenant_name || tenancy.tenant_id?.slice(0, 8) || '-'}
                            </span>
                          </div>
                        </td>
                        <td className="p-4 font-medium text-slate-900">
                          £{tenancy.rent_amount?.toLocaleString() || '-'}/pcm
                        </td>
                        <td className="p-4 text-slate-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {tenancy.start_date ? new Date(tenancy.start_date).toLocaleDateString() : '-'}
                          </div>
                        </td>
                        <td className="p-4 text-slate-600">
                          {tenancy.end_date ? new Date(tenancy.end_date).toLocaleDateString() : 'Periodic'}
                        </td>
                        <td className="p-4">
                          <Badge className={getStatusBadge(tenancy.status)}>
                            {tenancy.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
